/* eslint-disable camelcase */
import ClassificationAdditionalQuestion from 'components/inspection/ClassificationAdditionalQuestion';
import ClassificationFinalCheckN from 'components/inspection/ClassificationFinalCheckN';
import ClassificationGuide1 from 'components/inspection/ClassificationGuide1';
import ClassificationGuide2Part1 from 'components/inspection/ClassificationGuide2Part1';
import ClassificationGuide2Part2 from 'components/inspection/ClassificationGuide2Part2';
import ClassificationGuide2Part3 from 'components/inspection/ClassificationGuide2Part3';
import ClassificationGuide2Part4 from 'components/inspection/ClassificationGuide2Part4';
import ClassificationGuide3 from 'components/inspection/ClassificationGuide3';
import ClassificationGuide3End from 'components/inspection/ClassificationGuide3End';
import ClassificationGuide3NextGroup from 'components/inspection/ClassificationGuide3NextGroup';
import ClassificationGuideCmnEnd from 'components/inspection/ClassificationGuideCmnEnd';
import ClassificationGuideComplete from 'components/inspection/ClassificationGuideComplete';
import ClassificationGuideFinal from 'components/inspection/ClassificationGuideFinal';
import DocumentViewerCmn from 'components/inspection/player/DocumentViewerCmn';
import useInspection from 'hooks/useInspection';
// import { Detail3PropsType } from 'interface/inspection/InspectionInfo'; // TODO: 새로 만들 예정
import { ICmnInspectionProps } from 'interface/inspection/InspectionInfo';
import { IInspectionIpsativesProps } from 'interface/project/projectInfo';
import { handleCategoryInitData } from 'libs/mindUtils';
// import { getKeysWithArrays } from 'libs/mindUtils'; // TODO: 새로 만들 예정
import { memo, useMemo, useEffect } from 'react';
import { useAppDispatch } from 'store';
import { postInspectionClickLogs } from 'store/inspection/inspectionSlice';
import { inspectionLogger } from 'utils/inspectionLogger';
import { useProject } from 'hooks/useProject';

function InspectionPage(): JSX.Element {
  const dispatch = useAppDispatch();
  const { urlProjectId } = useProject();
  const {
    classification,
    inspectionDetail,
    // inspectionDetail2Info, // 제거됨 - 새로 만들 예정
    // inspectionDetail3Info2, // 제거됨 - 새로 만들 예정
    isClassificationGuideStart1,
    isClassificationGuideStart2_1,
    isClassificationGuideStart2_2,
    isClassificationGuideStart2_3,
    isClassificationGuideStart2_4,
    isClassificationGuideStart3,
    isClassificationGuideStartFinal,
    isClassificationGuideStartComplete,
    // inspectionResult, // 제거됨 - 새로 만들 예정
    // inspectionResult2Info, // 제거됨 - 새로 만들 예정
    // inspectionResult3Info, // 제거됨 - 새로 만들 예정
    inspectionFirstClassification,
    inspectionSecondClassification,
    inspectionThirdClassification,
    detailProgressInfo,
    objectKeyIndex,
  } = useInspection();

  // 주기적으로 클릭 로그 전송 (30초마다)
  useEffect(() => {
    if (!urlProjectId) return;

    const intervalId = setInterval(() => {
      // 비동기로 전송 (백그라운드에서 실행)
      inspectionLogger.flush().then((pendingLogs) => {
        if (pendingLogs.length > 0) {
          dispatch(postInspectionClickLogs({ projectId: urlProjectId, logs: pendingLogs }))
            .unwrap()
            .then(() => {
              console.log(`✅ 클릭 로그 ${pendingLogs.length}개 주기적 전송 완료`);
            })
            .catch((error) => {
              console.error('❌ 클릭 로그 주기적 전송 실패:', error);
              // 실패 시 로그를 다시 추가하지 않음 (다음 주기에 재시도)
            });
        }
      });
    }, 30 * 1000); // 30초

    return () => clearInterval(intervalId);
  }, [dispatch, urlProjectId]);

  // 1단계 분류 결과는 inspectionFirstClassification에서 가져옴
  // 2단계 분류 결과는 inspectionSecondClassification에서 가져옴
  const surveyResult = useMemo(() => {
    if (classification === '1') {
      // 1단계에서는 모든 분류 결과를 합쳐서 반환
      return [
        ...inspectionFirstClassification.NNTemp,
        ...inspectionFirstClassification.NTemp,
        ...inspectionFirstClassification.ATemp,
        ...inspectionFirstClassification.AATemp,
      ];
    }
    if (classification === '2_1') {
      // AA 그룹의 2단계 분류 결과
      return [
        ...inspectionSecondClassification.AATempNN,
        ...inspectionSecondClassification.AATempN,
        ...inspectionSecondClassification.AATempA,
        ...inspectionSecondClassification.AATempAA,
      ];
    }
    if (classification === '2_2') {
      // A 그룹의 2단계 분류 결과
      return [
        ...inspectionSecondClassification.ATempNN,
        ...inspectionSecondClassification.ATempN,
        ...inspectionSecondClassification.ATempA,
        ...inspectionSecondClassification.ATempAA,
      ];
    }
    if (classification === '2_3') {
      // NN 그룹의 2단계 분류 결과
      return [
        ...inspectionSecondClassification.NNTempNN,
        ...inspectionSecondClassification.NNTempN,
        ...inspectionSecondClassification.NNTempA,
        ...inspectionSecondClassification.NNTempAA,
      ];
    }
    if (classification === '2_4') {
      // N 그룹의 2단계 분류 결과
      return [
        ...inspectionSecondClassification.NTempNN,
        ...inspectionSecondClassification.NTempN,
        ...inspectionSecondClassification.NTempA,
        ...inspectionSecondClassification.NTempAA,
      ];
    }
    return [];
  }, [classification, inspectionFirstClassification, inspectionSecondClassification]);

  const handleResult = useMemo(() => {
    return handleCategoryInitData.map(obj => {
      let count = 0;
      let items: ICmnInspectionProps[] = [];

      if (classification === '1') {
        // 1단계 분류에서는 inspectionFirstClassification에서 가져옴
        if (obj.id === 'NN') {
          count = inspectionFirstClassification.NNTemp.length;
          items = inspectionFirstClassification.NNTemp;
        } else if (obj.id === 'N') {
          count = inspectionFirstClassification.NTemp.length;
          items = inspectionFirstClassification.NTemp;
        } else if (obj.id === 'A') {
          count = inspectionFirstClassification.ATemp.length;
          items = inspectionFirstClassification.ATemp;
        } else if (obj.id === 'AA') {
          count = inspectionFirstClassification.AATemp.length;
          items = inspectionFirstClassification.AATemp;
        }
      } else if (['2_1', '2_2', '2_3', '2_4'].includes(classification)) {
        // 2단계 분류에서는 inspectionSecondClassification에서 가져옴
        if (classification === '2_1') {
          // AA 그룹
          if (obj.id === 'NN') {
            count = inspectionSecondClassification.AATempNN.length;
            items = inspectionSecondClassification.AATempNN;
          } else if (obj.id === 'N') {
            count = inspectionSecondClassification.AATempN.length;
            items = inspectionSecondClassification.AATempN;
          } else if (obj.id === 'A') {
            count = inspectionSecondClassification.AATempA.length;
            items = inspectionSecondClassification.AATempA;
          } else if (obj.id === 'AA') {
            count = inspectionSecondClassification.AATempAA.length;
            items = inspectionSecondClassification.AATempAA;
          }
        } else if (classification === '2_2') {
          // A 그룹
          if (obj.id === 'NN') {
            count = inspectionSecondClassification.ATempNN.length;
            items = inspectionSecondClassification.ATempNN;
          } else if (obj.id === 'N') {
            count = inspectionSecondClassification.ATempN.length;
            items = inspectionSecondClassification.ATempN;
          } else if (obj.id === 'A') {
            count = inspectionSecondClassification.ATempA.length;
            items = inspectionSecondClassification.ATempA;
          } else if (obj.id === 'AA') {
            count = inspectionSecondClassification.ATempAA.length;
            items = inspectionSecondClassification.ATempAA;
          }
        } else if (classification === '2_3') {
          // NN 그룹
          if (obj.id === 'NN') {
            count = inspectionSecondClassification.NNTempNN.length;
            items = inspectionSecondClassification.NNTempNN;
          } else if (obj.id === 'N') {
            count = inspectionSecondClassification.NNTempN.length;
            items = inspectionSecondClassification.NNTempN;
          } else if (obj.id === 'A') {
            count = inspectionSecondClassification.NNTempA.length;
            items = inspectionSecondClassification.NNTempA;
          } else if (obj.id === 'AA') {
            count = inspectionSecondClassification.NNTempAA.length;
            items = inspectionSecondClassification.NNTempAA;
          }
        } else if (classification === '2_4') {
          // N 그룹
          if (obj.id === 'NN') {
            count = inspectionSecondClassification.NTempNN.length;
            items = inspectionSecondClassification.NTempNN;
          } else if (obj.id === 'N') {
            count = inspectionSecondClassification.NTempN.length;
            items = inspectionSecondClassification.NTempN;
          } else if (obj.id === 'A') {
            count = inspectionSecondClassification.NTempA.length;
            items = inspectionSecondClassification.NTempA;
          } else if (obj.id === 'AA') {
            count = inspectionSecondClassification.NTempAA.length;
            items = inspectionSecondClassification.NTempAA;
          }
        }
      } else if (classification === '3') {
        // 3단계 분류에서는 inspectionThirdClassification에서 가져옴
        // 현재 그룹 키 찾기
        const keys = Object.keys(inspectionThirdClassification);
        const parentKeys = new Set<string>();
        keys.forEach(key => {
          const parentKey = key.replace(/\d+$/, '');
          parentKeys.add(parentKey);
        });
        const parentKeysArray = Array.from(parentKeys);
        const currentGroupKey = parentKeysArray[objectKeyIndex] ?? '';
        
        if (currentGroupKey) {
          // 현재 그룹의 4개 하위 그룹에서 해당 value를 가진 항목들 찾기
          const groupKeys = [`${currentGroupKey}1`, `${currentGroupKey}2`, `${currentGroupKey}3`, `${currentGroupKey}4`];
          const allItems = groupKeys.flatMap(key => inspectionThirdClassification[key] || []);
          count = allItems.filter((tmpResult: ICmnInspectionProps) => tmpResult.value === obj.id).length;
          items = allItems.filter((tmpResult: ICmnInspectionProps) => tmpResult.value === obj.id);
        }
      } else {
        // 기타 단계에서는 surveyResult에서 필터링
        count = surveyResult.filter((tmpResult: ICmnInspectionProps) => tmpResult.value === obj.id).length;
        items = surveyResult.filter((tmpResult: ICmnInspectionProps) => tmpResult.value === obj.id);
      }

      return {
        ...obj,
        count,
        items,
      };
    });
  }, [classification, inspectionFirstClassification, inspectionSecondClassification, inspectionThirdClassification, objectKeyIndex, surveyResult]);

  // TODO: 새로 만들 예정
  // const AAStartIndex = useMemo(() => inspectionResult.findIndex((obj: any) => obj.value === 'AA') ?? -1, [inspectionResult]);
  // const AStartIndex = useMemo(() => inspectionResult.findIndex((obj: any) => obj.value === 'A') ?? -1, [inspectionResult]);
  // const NStartIndex = useMemo(() => inspectionResult.findIndex((obj: any) => obj.value === 'N') ?? -1, [inspectionResult]);
  // const NNStartIndex = useMemo(() => inspectionResult.findIndex((obj: any) => obj.value === 'NN') ?? -1, [inspectionResult]);
  const AAStartIndex = -1;
  const AStartIndex = -1;
  const NStartIndex = -1;
  const NNStartIndex = -1;

  // TODO: 새로 만들 예정
  const classificationConfig: Record<
    string,
    {
      startIndex: number;
      info: IInspectionIpsativesProps[];
      guideComponent: JSX.Element;
      guideStart: boolean;
      type: 'NN' | 'N' | 'A' | 'AA';
    }
  > = {
    '2_1': {
      startIndex: AAStartIndex,
      // 2_1 단계에서는 inspectionFirstClassification.AATemp를 사용
      info: inspectionFirstClassification.AATemp.map(item => ({
        id: item.id,
        inspectionId: item.inspectionId,
        content: item.content,
        saveFileName: item.saveFileName,
        regId: item.regId,
      })),
      guideComponent: <ClassificationGuide2Part1 />,
      guideStart: isClassificationGuideStart2_1,
      type: 'AA',
    },
    '2_2': {
      startIndex: AStartIndex,
      // 2_2 단계에서는 inspectionFirstClassification.ATemp를 사용
      info: inspectionFirstClassification.ATemp.map(item => ({
        id: item.id,
        inspectionId: item.inspectionId,
        content: item.content,
        saveFileName: item.saveFileName,
        regId: item.regId,
      })),
      guideComponent: <ClassificationGuide2Part2 />,
      guideStart: isClassificationGuideStart2_2,
      type: 'A',
    },
    '2_3': {
      startIndex: NNStartIndex,
      // 2_3 단계에서는 inspectionFirstClassification.NNTemp를 사용
      info: inspectionFirstClassification.NNTemp.map(item => ({
        id: item.id,
        inspectionId: item.inspectionId,
        content: item.content,
        saveFileName: item.saveFileName,
        regId: item.regId,
      })),
      guideComponent: <ClassificationGuide2Part3 />,
      guideStart: isClassificationGuideStart2_3,
      type: 'NN',
    },
    '2_4': {
      startIndex: NStartIndex,
      // 2_4 단계에서는 inspectionFirstClassification.NTemp를 사용
      info: inspectionFirstClassification.NTemp.map(item => ({
        id: item.id,
        inspectionId: item.inspectionId,
        content: item.content,
        saveFileName: item.saveFileName,
        regId: item.regId,
      })),
      guideComponent: <ClassificationGuide2Part4 />,
      guideStart: isClassificationGuideStart2_4,
      type: 'N',
    },
  };
  const config = classificationConfig[classification];
  // 2단계 분류에서 해당 그룹의 분류된 항목들을 필터링
  const filteredSurveyResult = useMemo(() => {
    if (!config) return [];
    
    if (classification === '2_1') {
      // AA 그룹의 모든 분류된 항목
      return [
        ...inspectionSecondClassification.AATempNN,
        ...inspectionSecondClassification.AATempN,
        ...inspectionSecondClassification.AATempA,
        ...inspectionSecondClassification.AATempAA,
      ];
    } else if (classification === '2_2') {
      // A 그룹의 모든 분류된 항목
      return [
        ...inspectionSecondClassification.ATempNN,
        ...inspectionSecondClassification.ATempN,
        ...inspectionSecondClassification.ATempA,
        ...inspectionSecondClassification.ATempAA,
      ];
    } else if (classification === '2_3') {
      // NN 그룹의 모든 분류된 항목
      return [
        ...inspectionSecondClassification.NNTempNN,
        ...inspectionSecondClassification.NNTempN,
        ...inspectionSecondClassification.NNTempA,
        ...inspectionSecondClassification.NNTempAA,
      ];
    } else if (classification === '2_4') {
      // N 그룹의 모든 분류된 항목
      return [
        ...inspectionSecondClassification.NTempNN,
        ...inspectionSecondClassification.NTempN,
        ...inspectionSecondClassification.NTempA,
        ...inspectionSecondClassification.NTempAA,
      ];
    }
    return [];
  }, [classification, config, inspectionSecondClassification]);

  // 3단계 분류: inspectionSecondClassification에서 20% 넘는 그룹들의 키 가져오기
  const checkOverKeys = useMemo(() => {
    if (classification !== '3') return [];
    
    const totalItems = inspectionDetail.inspectionIpsatives.length;
    const twentyPercentThreshold = Math.ceil(totalItems * 0.2);
    
    const groupsOver20Percent: string[] = [];
    const secondClassificationKeys: (keyof typeof inspectionSecondClassification)[] = [
      'NNTempNN',
      'NNTempN',
      'NNTempA',
      'NNTempAA',
      'NTempNN',
      'NTempN',
      'NTempA',
      'NTempAA',
      'ATempNN',
      'ATempN',
      'ATempA',
      'ATempAA',
      'AATempNN',
      'AATempN',
      'AATempA',
      'AATempAA',
    ];

    secondClassificationKeys.forEach(key => {
      const group = inspectionSecondClassification[key];
      if (group.length >= twentyPercentThreshold) {
        groupsOver20Percent.push(key);
      }
    });

    return groupsOver20Percent;
  }, [classification, inspectionDetail.inspectionIpsatives.length, inspectionSecondClassification]);

  // 현재 그룹의 정보
  const currentKeyInfo = checkOverKeys[objectKeyIndex] ?? '';
  
  // 현재 그룹의 원본 데이터: inspectionSecondClassification에서 20% 넘는 그룹의 문항들 가져오기
  const currentGroupData = useMemo(() => {
    if (!currentKeyInfo || classification !== '3') return [];
    // 2차 분류에서 해당 그룹의 데이터 가져오기
    const groupData = inspectionSecondClassification[currentKeyInfo as keyof typeof inspectionSecondClassification] || [];
    console.log(`[3단계] currentKeyInfo: ${currentKeyInfo}, currentGroupData.length: ${groupData.length}`);
    return groupData;
  }, [currentKeyInfo, inspectionSecondClassification, classification]);
  
  // 현재 그룹의 3차 분류 결과 가져오기 (이미 분류된 항목들)
  const currentGroupResult = useMemo(() => {
    if (!currentKeyInfo || classification !== '3') return [];
    const groupKeys = [`${currentKeyInfo}1`, `${currentKeyInfo}2`, `${currentKeyInfo}3`, `${currentKeyInfo}4`];
    const result = groupKeys.flatMap(key => inspectionThirdClassification[key] || []);
    console.log(`[3단계] currentGroupResult.length: ${result.length}`);
    return result;
  }, [currentKeyInfo, inspectionThirdClassification, classification]);
  
  const currentKeyArrayLenfth = currentGroupResult.length;
  // TODO: 새로 만들 예정
  // const handleResult3 = useMemo(() => {
  //   return handleCategoryInitData.map(obj => ({
  //     ...obj,
  //     // count:
  //     //   inspectionResult3Info[currentKeyInfo as Detail3PropsType]?.filter((tmpResult: IClassificationProps) => tmpResult.value === obj.id)
  //     //     ?.length || 0,
  //     // items:
  //     //   inspectionResult3Info[currentKeyInfo as Detail3PropsType]?.filter((tmpResult: IClassificationProps) => tmpResult.value === obj.id) ||
  //     //   [],
  //     count: 0,
  //     items: [],
  //   }));
  // }, []);
  return (
    <>
      {classification === '1' && !isClassificationGuideStart1 && <ClassificationGuide1 />}
      {classification === '1' &&
        isClassificationGuideStart1 &&
        surveyResult.length !== inspectionDetail.inspectionIpsatives.length && (
          <DocumentViewerCmn
            info={inspectionDetail.inspectionIpsatives}
            result={surveyResult}
            categories={handleResult}
            version="left1"
          />
        )}
      {classification === '1' &&
        isClassificationGuideStart1 &&
        surveyResult.length === inspectionDetail.inspectionIpsatives.length && (
          <ClassificationGuideCmnEnd checkType="1" />
        )}
      {/* 2단계 가이드 화면 표시 */}
      {config && !config.guideStart && config.guideComponent}

      {/* 2단계 분류 진행 화면: 모든 문항이 분류되지 않았거나, 마지막 문항 위치가 아니면 계속 진행 */}
      {config && config.guideStart && (filteredSurveyResult.length !== config.info.length || detailProgressInfo < config.info.length - 1) && (
        <DocumentViewerCmn
          info={config.info}
          result={surveyResult}
          categories={handleResult}
          type={config.type}
          version="left2"
        />
      )}

      {/* 2단계 분류 완료: 모든 문항이 분류되었고, 현재 위치가 마지막 문항일 때만 완료 화면 */}
      {config && config.guideStart && filteredSurveyResult.length === config.info.length && detailProgressInfo >= config.info.length - 1 && (
        <ClassificationGuideCmnEnd checkType={classification} />
      )}
      {classification === '3' && !isClassificationGuideStart3 && checkOverKeys.length > 0 && <ClassificationGuide3 count={checkOverKeys.length} />}
      {classification === '3' && 
        isClassificationGuideStart3 && 
        currentKeyInfo && 
        currentGroupData.length > 0 && 
        currentKeyArrayLenfth !== currentGroupData.length && (
        <DocumentViewerCmn
          info={currentGroupData.map(item => ({
            id: item.id,
            inspectionId: item.inspectionId,
            content: item.content,
            saveFileName: item.saveFileName,
            regId: item.regId,
          }))}
          result={currentGroupResult}
          categories={handleResult}
          type={currentKeyInfo as 'NN' | 'N' | 'A' | 'AA'}
          total={checkOverKeys.length}
          version="left3"
        />
      )}
      {classification === '3' &&
        isClassificationGuideStart3 &&
        currentKeyInfo &&
        currentGroupData.length > 0 &&
        currentKeyArrayLenfth === currentGroupData.length &&
        objectKeyIndex + 1 < checkOverKeys.length && (
          <ClassificationGuide3NextGroup next={objectKeyIndex + 1} total={checkOverKeys.length} />
        )}
      {classification === '3' &&
        isClassificationGuideStart3 &&
        currentKeyInfo &&
        currentGroupData.length > 0 &&
        currentKeyArrayLenfth === currentGroupData.length &&
        objectKeyIndex + 1 >= checkOverKeys.length && <ClassificationGuide3End />}
      {classification === 'final' && !isClassificationGuideStartFinal && <ClassificationGuideFinal />}
      {classification === 'final' && isClassificationGuideStartFinal && <ClassificationFinalCheckN />}
      {classification === 'additional' && <ClassificationAdditionalQuestion />}
      {classification === 'complete' && !isClassificationGuideStartComplete && (
        <ClassificationGuideComplete title="진단 종료" />
      )}
    </>
  );
}

export default memo(InspectionPage);
